import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, MessageSquare, Flag, FileText, Search, Filter, Volume2, VolumeX, Bookmark } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { VerifiedBadge } from "@/components/VerifiedBadge";

function VideoCard({ v, onLike, onMessage, onReport, onResume, muted, setMuted, canShortlist, isShortlisted, onShortlist }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            el.play().catch(() => {});
            api.post(`/videos/${v.id}/view`).catch(() => {});
          } else el.pause();
        });
      },
      { threshold: 0.6 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [v.id]);

  const authorMeta = useMemo(() => {
    const primary = (v.user_professions || []).find((p) => p.priority === "primary")?.name;
    return [primary || v.user_sector, v.user_location].filter(Boolean).join(" • ");
  }, [v.user_professions, v.user_sector, v.user_location]);

  return (
    <div className="vc-feed-item h-[calc(100vh-3.5rem-4rem)] sm:h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-3" data-testid={`video-card-${v.id}`}>
      <div className="relative w-full max-w-md h-full bg-black overflow-hidden">
        <video
          ref={ref}
          src={fileUrl(v.video_file_id)}
          poster={v.thumbnail_url ? `${process.env.REACT_APP_BACKEND_URL}${v.thumbnail_url}` : undefined}
          loop muted={muted} playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="video-el"
        />

        <button onClick={() => setMuted(!muted)} className="absolute top-4 left-4 bg-black/50 backdrop-blur text-white p-2" data-testid="mute-toggle">
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>

        {v.moderation_status === "flagged" && (
          <div className="absolute top-4 right-4 bg-[#D96C5B] text-white text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-1">Under review</div>
        )}

        <div className="absolute right-3 bottom-28 flex flex-col gap-4 items-center">
          <button onClick={onLike} className="flex flex-col items-center text-white" data-testid={`like-btn-${v.id}`}>
            <div className="w-10 h-10 bg-black/40 backdrop-blur flex items-center justify-center"><Heart className="w-5 h-5" /></div>
            <span className="text-xs mt-1">{v.likes || 0}</span>
          </button>
          {canShortlist && (
            <button onClick={onShortlist} className="flex flex-col items-center text-white" data-testid={`shortlist-btn-${v.id}`}>
              <div className={`w-10 h-10 backdrop-blur flex items-center justify-center ${isShortlisted ? "bg-[#F2C94C] text-[#1A1E1C]" : "bg-black/40"}`}>
                <Bookmark className={`w-5 h-5 ${isShortlisted ? "fill-current" : ""}`} />
              </div>
              <span className="text-xs mt-1">{isShortlisted ? "Saved" : "Save"}</span>
            </button>
          )}
          <button onClick={onMessage} className="flex flex-col items-center text-white" data-testid={`dm-btn-${v.id}`}>
            <div className="w-10 h-10 bg-black/40 backdrop-blur flex items-center justify-center"><MessageSquare className="w-5 h-5" /></div>
            <span className="text-xs mt-1">DM</span>
          </button>
          <button onClick={onResume} className="flex flex-col items-center text-white" data-testid={`resume-btn-${v.id}`}>
            <div className="w-10 h-10 bg-[#1E3F33] flex items-center justify-center"><FileText className="w-5 h-5" /></div>
            <span className="text-xs mt-1">Resume</span>
          </button>
          <button onClick={onReport} className="flex flex-col items-center text-white/80" data-testid={`report-btn-${v.id}`}>
            <div className="w-10 h-10 bg-black/40 backdrop-blur flex items-center justify-center"><Flag className="w-4 h-4" /></div>
            <span className="text-[10px] mt-1">Report</span>
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 via-black/40 to-transparent text-white">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/20 backdrop-blur overflow-hidden flex items-center justify-center text-sm font-bold">
              {v.user_avatar ? <img src={v.user_avatar.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${v.user_avatar}` : v.user_avatar} alt="" className="w-full h-full object-cover" /> : (v.user_name?.[0] || "?")}
            </div>
            <div>
              <Link to={`/p/${v.user_id}`} className="font-bold tracking-tight flex items-center gap-1.5 hover:underline" data-testid={`author-link-${v.id}`}>
                {v.user_name} {v.user_verified && <VerifiedBadge />}
              </Link>
              <div className="text-xs opacity-80">{authorMeta}</div>
            </div>
          </div>
          <div className="text-lg font-semibold mt-3 leading-tight">{v.title}</div>
          {v.description && <div className="text-sm opacity-90 mt-1 line-clamp-2">{v.description}</div>}
          <div className="mt-2 flex flex-wrap gap-2">
            {(v.hashtags || []).slice(0, 5).map((h) => (
              <span key={h} className="text-xs bg-white/15 backdrop-blur px-2 py-0.5">#{h}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const [search, setSearch] = useState(params.get("q") || "");
  const [hashtag, setHashtag] = useState(params.get("hashtag") || "");
  const [profession, setProfession] = useState(params.get("profession") || "all");
  const [professions, setProfessions] = useState([]);
  const [shortlisted, setShortlisted] = useState(new Set());
  const [dmTarget, setDmTarget] = useState(null);
  const [dmBody, setDmBody] = useState("");
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState("");

  const canShortlist = user?.role === "hr" || user?.role === "owner";

  const load = async (prof = profession) => {
    setLoading(true);
    try {
      const { data } = await api.get("/videos", {
        params: {
          q: search || undefined,
          hashtag: hashtag || undefined,
          profession: prof && prof !== "all" ? prof : undefined,
        },
      });
      setVideos(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/professions").then(({ data }) => setProfessions(data)).catch((err) => console.warn("Failed to load professions", err));
    if (canShortlist) {
      api.get("/shortlist/ids").then(({ data }) => setShortlisted(new Set(data.ids))).catch((err) => console.warn("Failed to load shortlist", err));
    }
    // eslint-disable-next-line
  }, []);

  const applyFilters = (prof = profession) => {
    setParams({ q: search, hashtag, profession: prof === "all" ? "" : prof });
    load(prof);
  };

  const onProfessionChange = (v) => {
    setProfession(v);
    applyFilters(v);
  };

  const onLike = async (v) => {
    try {
      const { data } = await api.post(`/videos/${v.id}/like`);
      setVideos((vs) => vs.map((x) => x.id === v.id ? { ...x, likes: (x.likes || 0) + (data.liked ? 1 : -1) } : x));
    } catch { toast.error("Could not like"); }
  };

  const toggleShortlist = async (v) => {
    try {
      const { data } = await api.post(`/shortlist/${v.user_id}`);
      setShortlisted((prev) => {
        const s = new Set(prev);
        if (data.shortlisted) s.add(v.user_id); else s.delete(v.user_id);
        return s;
      });
      toast.success(data.shortlisted ? "Saved to shortlist" : "Removed from shortlist");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendDM = async (isResumeRequest = false) => {
    if (!dmTarget) return;
    try {
      await api.post("/messages", {
        to_user_id: dmTarget.user_id,
        body: dmBody || (isResumeRequest ? `Hi ${dmTarget.user_name}, could you share your resume?` : ""),
        is_resume_request: isResumeRequest,
      });
      toast.success("Message sent");
      setDmTarget(null); setDmBody("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const submitReport = async () => {
    try {
      await api.post("/reports", { video_id: reportTarget.id, reason: reportReason });
      toast.success("Reported. Our team will review.");
      setReportTarget(null); setReportReason("");
    } catch { toast.error("Failed to report"); }
  };

  const viewResume = async (v) => {
    try {
      const { data } = await api.get(`/users/${v.user_id}`);
      if (data.resume_file_id) {
        window.open(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.resume_file_id}`, "_blank");
      } else {
        setDmTarget({ user_id: v.user_id, user_name: v.user_name });
        setDmBody(`Hi ${v.user_name}, could you share your resume?`);
        toast.message("No resume on profile — sending a request via DM");
      }
    } catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="sticky top-14 z-30 bg-white/85 backdrop-blur-xl border-b border-[#E2E4E2]">
        <div className="max-w-md sm:max-w-3xl mx-auto px-4 py-3 flex gap-2 flex-wrap sm:flex-nowrap">
          <div className="relative flex-1 min-w-40">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#68706B]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search titles, hashtags, skills" className="pl-9 rounded-none h-10" data-testid="feed-search" />
          </div>
          <Select value={profession} onValueChange={onProfessionChange}>
            <SelectTrigger className="rounded-none h-10 w-44" data-testid="feed-profession-filter"><SelectValue placeholder="Profession" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All professions</SelectItem>
              {professions.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={hashtag} onChange={(e) => setHashtag(e.target.value)} placeholder="#hashtag" className="rounded-none h-10 w-28" data-testid="feed-hashtag" />
          <Button onClick={() => applyFilters()} className="rounded-none bg-[#1E3F33] hover:bg-[#152C24] h-10 px-4" data-testid="feed-apply"><Filter className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="vc-feed overflow-y-scroll h-[calc(100vh-3.5rem-3.25rem)]" data-testid="feed-list">
        {loading && <div className="p-12 text-center text-[#68706B]">Loading…</div>}
        {!loading && videos.length === 0 && (
          <div className="p-12 text-center text-[#68706B]">
            <p>No visual resumes yet matching your filters.</p>
            {user?.role === "candidate" && (
              <Button onClick={() => navigate("/upload")} className="mt-4 rounded-none bg-[#1E3F33]" data-testid="feed-cta-upload">Upload yours</Button>
            )}
          </div>
        )}
        {videos.map((v) => (
          <VideoCard
            key={v.id}
            v={v}
            muted={muted}
            setMuted={setMuted}
            canShortlist={canShortlist}
            isShortlisted={shortlisted.has(v.user_id)}
            onShortlist={() => toggleShortlist(v)}
            onLike={() => onLike(v)}
            onMessage={() => { setDmTarget({ user_id: v.user_id, user_name: v.user_name }); setDmBody(""); }}
            onResume={() => viewResume(v)}
            onReport={() => setReportTarget(v)}
          />
        ))}
      </div>

      <Dialog open={!!dmTarget} onOpenChange={(o) => !o && setDmTarget(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Message {dmTarget?.user_name}</DialogTitle></DialogHeader>
          <Textarea value={dmBody} onChange={(e) => setDmBody(e.target.value)} placeholder="Write a short professional message…" className="rounded-none min-h-32" data-testid="dm-body" />
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => sendDM(true)} data-testid="dm-request-resume">Request resume</Button>
            <Button className="rounded-none bg-[#1E3F33]" onClick={() => sendDM(false)} data-testid="dm-send">Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reportTarget} onOpenChange={(o) => !o && setReportTarget(null)}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>Report this video</DialogTitle></DialogHeader>
          <Textarea value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="Why? (politics, propaganda, entertainment, etc.)" className="rounded-none min-h-24" data-testid="report-reason" />
          <DialogFooter>
            <Button className="rounded-none bg-[#D96C5B] hover:bg-[#B85948]" onClick={submitReport} data-testid="report-submit">Submit report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
